// The pure half of the `{Slicer.` menu: when it opens, what a pick writes, and what each
// key does. The adapters in `AddWidgetModal/shared` and `TextConfigurator` only translate
// DOM and ProseMirror events into these calls, so the node runner can pin every rule here
// (the arrangement of `Processing/countSpec.mjs`).

import { normalizeKey } from "./slicerKeys.mjs";

/**
 * The text from the last `{` to the caret. Group 1 is the level-1 key partial; groups 2
 * and 3 are the level-2 key and variable partial. The level-2 branch needs one key
 * character, so `{Slicer..` does not match.
 */
export const TRIGGER_RE = /^\{\s*slicer\.(?:([a-z0-9]*)|([a-z0-9]+)\.([a-z]*))$/i;

/** A character after the caret that could continue the token keeps the menu closed. */
const AFTER_GUARD_RE = /^[A-Za-z0-9.:+\-}]/;

/** ProseMirror emits no text for a leaf node; the sentinel keeps every offset honest. */
export const LEAF_SENTINEL = "￼";

/**
 * The unfinished slicer token the caret sits in, or null.
 *
 * `before` is the text from the start of the field or textblock to the caret, `after`
 * the text from the caret to its end. The result carries the level, the typed key and
 * partial as typed, and `brace`, the offset of the opening brace inside `before`.
 */
export function parseSlicerTrigger(before, after) {
  if (typeof before !== "string") return null;
  if (typeof after === "string" && AFTER_GUARD_RE.test(after)) return null;
  const brace = before.lastIndexOf("{");
  if (brace < 0) return null;
  const match = TRIGGER_RE.exec(before.slice(brace));
  if (!match) return null;
  if (match[2] != null) {
    return { level: 2, key: match[2], partial: match[3] ?? "", brace };
  }
  return { level: 1, key: null, partial: match[1] ?? "", brace };
}

/** The level-1 pick: the text that replaces the partial and opens level 2. */
export function completeSlicerToken(key) {
  return `{Slicer.${normalizeKey(key)}.`;
}

/** The level-2 pick: a complete token, or one with an empty format slot. */
export function slicerToken(key, variable, formatSlot = false) {
  return `{Slicer.${normalizeKey(key)}.${variable}${formatSlot ? ":" : ""}}`;
}

/** Where the caret lands after a level-2 pick: after the brace, or inside the format slot. */
export function slicerTokenCaret(text, formatSlot = false) {
  return formatSlot ? text.length - 1 : text.length;
}

/**
 * `value` with `[from, to)` replaced by `text`, or null when the result would exceed
 * `maxLength`. The native `maxLength` attribute does not limit a programmatic value, so
 * the adapter refuses the pick instead.
 */
export function spliceText(value, from, to, text, maxLength) {
  const source = typeof value === "string" ? value : "";
  const start = Math.max(0, Math.min(from, source.length));
  const end = Math.max(start, Math.min(to, source.length));
  const next = source.slice(0, start) + text + source.slice(end);
  if (typeof maxLength === "number" && next.length > maxLength) return null;
  return next;
}

/**
 * The caret text of a ProseMirror selection, or null unless it is an empty text selection
 * inside a textblock. `base` is the document position of the textblock's first character,
 * so `base + brace` is the document position of the opening brace.
 */
export function textblockCaretText(selection) {
  const $cursor = selection?.$cursor;
  if (!$cursor || !$cursor.parent?.isTextblock) return null;
  const parent = $cursor.parent;
  const offset = $cursor.parentOffset;
  return {
    before: parent.textBetween(0, offset, undefined, LEAF_SENTINEL),
    after: parent.textBetween(offset, parent.content.size, undefined, LEAF_SENTINEL),
    base: $cursor.start(),
  };
}

export const CLOSED_MENU = Object.freeze({ open: false, dismissed: null });

function sameTrigger(a, b) {
  return a.level === b.level && a.key === b.key && a.partial === b.partial;
}

/**
 * The next menu state after a parse. `trigger` is `parseSlicerTrigger(before, after)`
 * and `caret` the `{ before, after, base }` it was parsed from. The menu stays closed
 * after Esc while the text from the opening brace to the caret is unchanged at the same
 * position; a filter change resets the active row to the first match.
 */
export function menuStep(prev, trigger, caret) {
  if (!trigger || !caret) return CLOSED_MENU;
  const brace = caret.base + trigger.brace;
  const text = caret.before.slice(trigger.brace);
  const dismissed = prev?.dismissed;
  if (dismissed && dismissed.brace === brace && dismissed.text === text) return prev;
  const keepActive = prev?.open && sameTrigger(prev, trigger);
  return {
    open: true,
    level: trigger.level,
    key: trigger.key,
    partial: trigger.partial,
    brace,
    end: caret.base + caret.before.length,
    text,
    activeIndex: keepActive ? prev.activeIndex : 0,
    dismissed: null,
  };
}

/** The closed state that remembers the dismissed text, so the same partial does not reopen. */
export function dismissMenu(prev) {
  if (!prev?.open) return CLOSED_MENU;
  return { open: false, dismissed: { brace: prev.brace, text: prev.text } };
}

/** The active row after a move, wrapped over the pickable rows. */
export function moveActive(index, delta, count) {
  if (count <= 0) return 0;
  return (((index + delta) % count) + count) % count;
}

/**
 * What a key does while the menu is open. Returns null when the key passes through to
 * the field: the menu is closed, a modifier is held, the key is part of a composition,
 * or the key is none of the menu's. Enter and Tab on a list without a pickable row close
 * the menu and still pass through.
 */
export function menuKey(state, event, pickable) {
  if (!state?.open || !event) return null;
  if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return null;
  switch (event.key) {
    case "ArrowDown":
      return pickable > 0 ? { type: "move", delta: 1 } : null;
    case "ArrowUp":
      return pickable > 0 ? { type: "move", delta: -1 } : null;
    case "Enter":
    case "Tab":
      if (pickable <= 0) return { type: "close", handled: false };
      return { type: "pick", formatSlot: state.level === 2 && event.key === "Enter" && !!event.shiftKey };
    case "Escape":
      return { type: "close", handled: true };
    default:
      return null;
  }
}
